import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  getUserInfo,
  listAddressOptions,
  updateIndirizzo,
  resolveOrganizationContext,
} from "../Admin/adminApi";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import CityField from "../components/CityField";
import IronIcon from "../components/icons/IronIcon";
import WasherIcon from "../components/icons/WasherIcon";
import BothIcon from "../components/icons/BothIcon";
import { fetchActivePromoImages } from "../api/promosApi";
import { SERVICE_CATEGORIES } from "../config/serviceCategories";
import { geolocateCity } from "../utils/geolocateCity";
import { resolveSupportedCity } from "../utils/resolveSupportedCity";
import "./css/Dashboard.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/* ---------------- PromoCarousel (hp-*) ---------------- */
function PromoCarousel({ slides, intervalMs = 4000 }) {
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!slides?.length) return;
    timerRef.current = setInterval(
      () => setIdx((i) => (i + 1) % slides.length),
      intervalMs,
    );
    return () => clearInterval(timerRef.current);
  }, [slides, intervalMs]);

  const onImgError = (e) => {
    e.currentTarget.src =
      "data:image/svg+xml;utf8," +
      encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 300"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop stop-color="#ff8a00" offset="0"/><stop stop-color="#e52e71" offset="1"/></linearGradient></defs><rect width="100%" height="100%" fill="url(#g)"/></svg>`,
      );
  };

  return (
    <section className="hp-carousel" aria-label="Promozioni">
      {slides.map((s, i) => (
        <div
          className={`hp-slide ${i === idx ? "is-active" : ""}`}
          key={s.id ?? i}
        >
          <img
            src={s.img}
            alt={s.alt || s.title || "Promo"}
            onError={onImgError}
          />
        </div>
      ))}
      <div className="hp-dots" role="tablist" aria-label="Indicatori slide">
        {slides.map((_, i) => (
          <button
            key={i}
            className={`hp-dot ${i === idx ? "is-active" : ""}`}
            aria-label={`Vai alla slide ${i + 1}`}
            onClick={() => setIdx(i)}
          />
        ))}
      </div>
    </section>
  );
}

function addressOptionValue(a) {
  return `${a.source_type}:${a.source_id}`;
}

/* ---------------- PAGE ---------------- */
export default function Dashboard() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [addresses, setAddresses] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  // città “manuale”
  const [cityInput, setCityInput] = useState("");
  const [cityConfirmed, setCityConfirmed] = useState(true);
  const [cityProvincia, setCityProvincia] = useState(""); // opzionale, ma utile se lo vuoi mostrare/riusare
  const [cityCap, setCityCap] = useState(""); // idem
  const cityEditVersion = useRef(0);

  const [servizio, setServizio] = useState(null);

  // immagine DEDICATA per la colonna destra (non legata alle promo)
  const [heroImgUrl, setHeroImgUrl] = useState("");
  const [heroAlt, setHeroAlt] = useState("");

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await getUserInfo(token);

        const list = await listAddressOptions(token);
        const safe = Array.isArray(list) ? list : [];

        setAddresses(safe);

        const active =
          safe.find((a) => a.source_type === "user" && a.active) ||
          safe.find((a) => a.active) ||
          safe[0] ||
          null;

        setSelectedId(active ? addressOptionValue(active) : null);
      } catch (e) {
        console.error(e);
        toast.error("Errore nel caricamento della dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const activeAddress = useMemo(
    () =>
      addresses.find((a) => addressOptionValue(a) === selectedId) ||
      addresses.find((a) => a.active) ||
      null,
    [addresses, selectedId],
  );

  const onChangeSelected = async (e) => {
    const value = e.target.value;
    setSelectedId(value);

    const chosen = addresses.find((a) => addressOptionValue(a) === value);
    if (!chosen) return;

    try {
      // Se è un indirizzo utente, manteniamo il vecchio comportamento
      if (chosen.source_type === "user") {
        await updateIndirizzo(
          Number(chosen.source_id),
          { active: true },
          token,
        );
      }

      // Se è organization non c'è updateIndirizzo da fare:
      // basta aggiornare la selezione locale, perché è un address option condiviso
      const fresh = await listAddressOptions(token);
      setAddresses(Array.isArray(fresh) ? fresh : []);

      toast.success("Indirizzo selezionato aggiornato");
    } catch (err) {
      console.error(err);
      toast.error("Errore aggiornamento indirizzo");
    }
  };

  const handleGeolocate = useCallback(() => {
    const requestVersion = ++cityEditVersion.current;
    geolocateCity()
      .then(resolveSupportedCity)
      .then(({ name, provincia, cap }) => {
        if (requestVersion !== cityEditVersion.current) return;
        setCityInput(name);
        setCityProvincia(provincia);
        setCityCap(cap);
        setCityConfirmed(true);
        toast.success("Geolocalizzazione completata");
      })
      .catch((err) => {
        if (requestVersion !== cityEditVersion.current) return;
        const code = err?.code;
        if (code === "UNSUPPORTED_CITY") {
          toast.error("Città non supportata: seleziona una città dall’elenco");
        } else if (code === "NO_CITY") {
          toast.error("Città non trovata");
        } else if (code === "UNSUPPORTED" || code === "DENIED") {
          toast.error("Geolocalizzazione non disponibile");
        } else {
          toast.error("Errore geolocalizzazione");
        }
      });
  }, []);

  const goToSearch = async (svc = servizio) => {
    const typedCity = cityInput && cityInput.trim();
    const city = typedCity || activeAddress?.citta || "";

    if (!city && activeAddress?.source_type !== "organization") {
      toast.error("Seleziona o inserisci una città");
      return;
    }

    if (typedCity && cityConfirmed !== true) {
      toast.error("Seleziona una città dall’elenco");
      return;
    }

    try {
      if (activeAddress?.source_type === "organization") {
        const ctx = await resolveOrganizationContext(
          {
            source_type: "organization",
            source_id: Number(activeAddress.source_id),
          },
          token,
        );

        if (ctx?.mode === "organization_supplier" && ctx?.supplier?.id) {
          navigate(`/servizi/${ctx.supplier.id}`, {
            state: {
              selectedAddress: {
                source_type: activeAddress.source_type,
                source_id: Number(activeAddress.source_id),
                label:
                  activeAddress.display_name ||
                  activeAddress.label ||
                  "Indirizzo",
                indirizzo:
                  activeAddress.display_name ||
                  activeAddress.label ||
                  "Indirizzo",
                street: activeAddress.street || "",
                cap: activeAddress.cap || "",
                citta: activeAddress.citta || "",
                provincia: activeAddress.provincia || "",
                active: !!activeAddress.active,
                organization_name: activeAddress.organization_name || "",
              },
              organizationContext: ctx,
            },
          });
          return;
        }
      }

      const qs = new URLSearchParams({
        citta: city,
        servizio: svc || "",
        auto: "1",
      }).toString();

      navigate(`/ricercaservizi?${qs}`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Errore apertura servizi");
    }
  };

  /* Slides promo per il carosello */
  const [slides, setSlides] = useState([
    {
      img: "https://images.unsplash.com/photo-1603252109303-2751441dd157?q=80&w=1600&auto=format&fit=crop",
    },
    {
      img: "https://images.unsplash.com/photo-1545239351-1141bd82e8a6?q=80&w=1600&auto=format&fit=crop",
    },
    {
      img: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?q=80&w=1600&auto=format&fit=crop",
    },
  ]);

  useEffect(() => {
    (async () => {
      try {
        const promos = await fetchActivePromoImages({
          token: localStorage.getItem("token"),
          limit: 6,
          city: (cityInput || activeAddress?.citta || "").trim(),
        });
        if (Array.isArray(promos) && promos.length) {
          const mapped = promos
            .filter((p) => p.image_url)
            .map((p) => ({
              id: p.id,
              img: p.image_url,
              title: p.code ? `Promo ${p.code}` : "",
            }));
          if (mapped.length) setSlides(mapped);
        }
      } catch (e) {
        console.error(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityInput, activeAddress?.citta]);

  // immagine “hero” dedicata (non promo)
  useEffect(() => {
    (async () => {
      if (!API_BASE) return;
      try {
        const r = await fetch(`${API_BASE}/api/ui/home-hero`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (r.ok) {
          const js = await r.json().catch(() => null);
          if (js?.image_url) {
            setHeroImgUrl(js.image_url);
            setHeroAlt(js.alt || "Visual");
          } else {
            setHeroImgUrl("");
            setHeroAlt("");
          }
        } else {
          setHeroImgUrl("");
          setHeroAlt("");
        }
      } catch {
        setHeroImgUrl("");
        setHeroAlt("");
      }
    })();
  }, [token]);

  const categories = useMemo(
    () =>
      SERVICE_CATEGORIES.map((c) => ({
        ...c,
        Icon:
          c.key === "lavaggio"
            ? WasherIcon
            : c.key === "stiratura"
            ? IronIcon
            : BothIcon,
      })),
    [],
  );

  if (loading) {
    return (
      <main className="hp-root">
        <div className="hp-page">
          <section className="hp-search">
            <div className="hp-searchGrid">
              <div className="hp-searchColLeft">
                <div className="hp-skel" />
                <div className="hp-skel" />
                <div className="hp-skel" />
              </div>
              <div className="hp-searchColRight">
                <div className="hp-heroPlaceholder" aria-hidden />
              </div>
            </div>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className="hp-root">
      <div className="hp-page">
        <PromoCarousel slides={slides} />

        {/* Search panel in due colonne (sx form, dx immagine dedicata) */}
        <section className="hp-search">
          <div className="hp-searchGrid">
            {/* Colonna SINISTRA */}
            <div className="hp-searchColLeft">
              <h2>Trova servizi vicino a te</h2>

              {/* Indirizzo salvato */}
              <div className="hp-field">
                <label>Il tuo indirizzo di consegna</label>
                <div className="hp-input hp-select hp-select--narrow">
                  <select
                    id="addr-select"
                    value={selectedId || ""}
                    onChange={onChangeSelected}
                    className="hp-select-el"
                    aria-label="Seleziona indirizzo attivo"
                  >
                    {addresses.length === 0 && (
                      <option value="">— Nessun indirizzo salvato —</option>
                    )}

                    {addresses.map((a) => {
                      const cityProv = [
                        a.citta,
                        a.provincia && `(${a.provincia})`,
                      ]
                        .filter(Boolean)
                        .join(" ");

                      const prefix =
                        a.source_type === "organization" ? "🏢" : "🏠";
                      const text =
                        `${prefix} ${
                          a.display_name || a.label || "Indirizzo"
                        }` + (cityProv ? ` - ${cityProv}` : "");

                      return (
                        <option
                          key={addressOptionValue(a)}
                          value={addressOptionValue(a)}
                        >
                          {text}
                        </option>
                      );
                    })}
                  </select>
                  <span className="hp-chevron" aria-hidden>
                    <ChevronDownIcon />
                  </span>
                </div>
              </div>

              {/* City input + geolocalizza */}
              <div className="hp-field">
                <label>Oppure cerca in una città</label>

                <div className="hp-cityRow">
                  <div className="hp-cityPill">
                    <CityField
                      label=""
                      value={cityInput}
                      provinciaValue={cityProvincia}
                      capValue={cityCap}
                      enabled={true}
                      hardConfirm={true}
                      confirmed={cityConfirmed}
                      setConfirmed={setCityConfirmed}
                      placeholder="Inserisci città"
                      onChangeCity={(v) => {
                        cityEditVersion.current++;
                        setCityInput(v);
                        setCityConfirmed(false);
                        setCityProvincia("");
                        setCityCap("");
                      }}
                      onApplyCity={({ name, provincia, cap }) => {
                        cityEditVersion.current++;
                        setCityInput(name);
                        setCityProvincia(provincia || "");
                        setCityCap(cap || "");
                        setCityConfirmed(true);
                      }}
                      showEmptyHint
                    />
                  </div>

                  <button
                    type="button"
                    className="hp-cityGeoBtn"
                    onClick={handleGeolocate}
                    title="Geolocalizza"
                    aria-label="Geolocalizza"
                  >
                    <PinIcon />
                  </button>
                </div>
              </div>

              {/* Pulsante azione */}
              <div className="hp-actions">
                <button
                  type="button"
                  className="hp-btnPrimary"
                  onClick={() => goToSearch()}
                  aria-label="Cerca servizi"
                >
                  Cerca
                </button>
              </div>
            </div>

            {/* Colonna DESTRA: solo immagine */}
            <div className="hp-searchColRight">
              {heroImgUrl ? (
                <img
                  className="hp-heroAbs"
                  src={heroImgUrl}
                  alt={heroAlt || "Immagine"}
                  loading="eager"
                />
              ) : (
                <div className="hp-heroPlaceholder" aria-hidden />
              )}
            </div>
          </div>
        </section>

        {/* Categorie disattivate temporaneamente */}
        {/*
        <section className="hp-categories" aria-label="Categorie">
          {categories.map((c) => (
            <figure key={c.key} className="hp-cat" aria-label={c.label}>
              <div
                className="hp-catImgWrap"
                role="button"
                tabIndex={0}
                onClick={() => {
                  setServizio(c.key);
                  goToSearch(c.key);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setServizio(c.key);
                    goToSearch(c.key);
                  }
                }}
              >
                <img
                  className="hp-catImg"
                  src={c.img}
                  alt={c.label}
                  loading="eager"
                />
                <div className="hp-catLabelIn">{c.label}</div>
                {servizio === c.key && (
                  <span className="hp-check" aria-hidden>
                    <CheckIcon />
                  </span>
                )}
              </div>
            </figure>
          ))}
        </section>
          */}
      </div>
    </main>
  );
}

/* ---------------- Icons inline ---------------- */
function ChevronDownIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function PinIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 21s7-4.35 7-11a7 7 0 10-14 0c0 6.65 7 11 7 11z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <circle cx="12" cy="10" r="2.5" fill="currentColor" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M20 6L9 17l-5-5"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
